import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Settings, ShoppingCart, Star, ChevronRight,
  X, Phone, ChevronLeft, MessageCircle, Zap, Eye,
  Tv, Play, Gift, TrendingUp, CreditCard, Gamepad2,
  DollarSign, Lock, Tag, Percent, User, LogIn, UserPlus,
  BookOpen, Video, FileText
} from "lucide-react";
import { useExchangeRate, formatUSD, formatHTG } from "./lib/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'smartechsystempro.com' || hostname === 'www.smartechsystempro.com') return `${protocol}//${hostname}/api`;
  if (hostname.includes('emergent.host') || hostname.includes('emergentagent.com')) return `${protocol}//${hostname}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
};
const API = getApiUrl();

const ADMIN_WHATSAPP = "50946261180";

const SERVICE_IMAGES = {
  netflix: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/yqcxueum_p-1-91108938-netflix-reasserts-dominance-new-model.webp",
  prime_video: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/j0xdbwmj_images%20%283%29.png",
  freefire: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/9cgv92ex_images%20%2833%29.jpeg",
  recharge_meru: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/tglk47e8_images%20%2828%29.jpeg",
  recharge_crypto: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/hr2k6vdg_images%20%2832%29.jpeg",
  recharge_binance: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/x0utmjp8_images%20%284%29.png",
  giftcard_apple: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/aupiwzey_images%20%2827%29.jpeg",
  boost_reseaux: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/pf4nary8_images%20%2831%29.jpeg",
  recharge_wise: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/d7e7s4y4_images%20%2830%29.jpeg",
  recharge_paypal: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/temgaznv_GettyImages-2227231024.jpg",
  recharge_fyatu: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/f4h6w226_images%20%2829%29.jpeg",
  echange_moncash_natcash: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/qyvhrc76_images%20%2834%29.jpeg",
};

const HERO_BG = "";

const SERVICE_ICONS = {
  tv: Tv, play: Play, gift: Gift, trending: TrendingUp,
  bitcoin: DollarSign, "credit-card": CreditCard, gamepad: Gamepad2,
  exchange: CreditCard
};

const QR_CODES = [
  { image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/7b1bzifc_IMG-20260320-WA0055%282%29.jpg", label: "MonCash", number: "46261180", name: "Pointdujour Frenson" },
  { image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/6vevjn52_IMG-20260320-WA0054%282%29.jpg", label: "Digicel", number: "50955460588", name: "FRENSON POINT-DU-JOUR" },
  { image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/59klzhb1_IMG-20260320-WA0053%283%29.jpg", label: "MonCash QR", number: "+50947096761", name: "Pointdujour Frenson" }
];
const PAYMENT_PHONE = "+50946261180";

const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const getLowestPrice = (service) => {
  if (!service.plans || service.plans.length === 0) return null;
  const prices = service.plans.filter(p => p.price > 0).map(p => p.price);
  if (prices.length === 0) {
    const rateP = service.plans.find(p => p.rate);
    if (rateP) return { type: "rate", rate: rateP.rate };
    return { type: "custom" };
  }
  return { type: "fixed", price: Math.min(...prices) };
};

const StoreFront = ({ onNavigate }) => {
  const exchangeRate = useExchangeRate();
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [popup, setPopup] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showQuickBuy, setShowQuickBuy] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [customAmountUsd, setCustomAmountUsd] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("qr");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [publicCourses, setPublicCourses] = useState([]);
  const [popupVideo, setPopupVideo] = useState(null);

  const isClientLoggedIn = () => !!localStorage.getItem("client_token");

  const fetchData = useCallback(async () => {
    const logErr = (where) => (e) => {
      if (process.env.NODE_ENV === "development") console.debug(`[StoreFront] ${where} failed:`, e?.message);
    };
    try { const r = await axios.get(`${API}/public/services`); setServices(r.data); } catch (e) { logErr("public/services")(e); }
    try { const r = await axios.get(`${API}/public/reviews`); setReviews(r.data); } catch (e) { logErr("public/reviews")(e); }
    try { const r = await axios.get(`${API}/public/explainer-video/popup`); if (r.data.video_url) setPopupVideo(r.data); } catch (e) { logErr("public/explainer-video")(e); }
    try {
      const r = await axios.get(`${API}/popup/active`);
      if (r.data) { setPopup(r.data); setShowPopup(true); }
    } catch (e) { logErr("popup/active")(e); }
    try { await axios.post(`${API}/track-visit`); } catch (e) { logErr("track-visit")(e); }
    try { const r = await axios.get(`${API}/public/courses`); setPublicCourses(r.data); } catch (e) { logErr("public/courses")(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBuyClick = (service, plan) => {
    if (!isClientLoggedIn()) {
      setSelectedService(service);
      setSelectedPlan(plan);
      setShowAuthPrompt(true);
      return;
    }
    // Bug fix 2026-06-06: logged-in clients must NEVER see the legacy quick-buy
    // modal (transaction_id + WhatsApp). Their proper checkout lives in the
    // client dashboard with image-proof upload, balance toggle, etc.
    // We forward the chosen service + plan via URL so the dashboard can open
    // the order form pre-filled.
    const sid = service?.id || "";
    const pid = plan?.id || "";
    const query = (sid || pid) ? `?service_id=${encodeURIComponent(sid)}&plan_id=${encodeURIComponent(pid)}` : "";
    onNavigate("client", query);
  };

  const calculatePrice = () => {
    if (!selectedPlan) return 0;
    if (selectedPlan.price > 0) return selectedPlan.price;
    if (selectedPlan.is_exchange && customAmountUsd) {
      const amount = parseFloat(customAmountUsd);
      const feeTable = selectedService?.fee_table || [];
      for (const tier of feeTable) {
        if (amount >= tier.min && amount <= tier.max) {
          const fee = selectedPlan.exchange_type === "moncash" ? tier.moncash_fee : tier.natcash_fee;
          return amount + fee;
        }
      }
      return amount;
    }
    if (selectedPlan.rate && customAmountUsd) return parseFloat(customAmountUsd) * selectedPlan.rate;
    return 0;
  };

  const handleSubmitOrder = async () => {
    if (!transactionId.trim()) { toast.error("Entrez l'ID de transaction"); return; }
    if (selectedPlan.price === 0 && !selectedPlan.is_exchange && !customAmountUsd) { toast.error("Entrez un montant"); return; }
    if (selectedPlan.is_exchange && (!customAmountUsd || parseFloat(customAmountUsd) < 250)) { toast.error("Montant minimum: 250 Gdes"); return; }
    if (selectedPlan.min_usd && parseFloat(customAmountUsd) < selectedPlan.min_usd) { toast.error(`Montant minimum: ${selectedPlan.min_usd}$`); return; }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("client_token");
      const res = await axios.post(`${API}/client/order`, {
        plan_id: selectedPlan.id,
        custom_amount_usd: customAmountUsd ? parseFloat(customAmountUsd) : null,
        transaction_id: transactionId.trim(),
        payment_method: paymentMethod
      }, { headers: { Authorization: `Bearer ${token}` } });
      const whatsappUrl = res.data.whatsapp_url || `https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2`;
      window.open(whatsappUrl, "_blank");
      toast.success("Commande envoyee!");
      setShowQuickBuy(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!isClientLoggedIn()) { setShowAuthPrompt(true); return; }
    if (!reviewComment.trim()) { toast.error("Ecrivez un commentaire"); return; }
    try {
      const token = localStorage.getItem("client_token");
      await axios.post(`${API}/reviews`, {
        rating: reviewRating,
        comment: reviewComment.trim(),
        name: reviewName || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Merci pour votre avis!");
      setShowReviewForm(false);
      setReviewComment("");
      setReviewRating(5);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const filteredServices = services.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StarRating = ({ rating, interactive, onRate }) => (
    <div className="sf-stars" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={interactive ? 24 : 14}
          fill={i <= rating ? "#FFD700" : "transparent"}
          color={i <= rating ? "#FFD700" : "#52525B"}
          onClick={() => interactive && onRate && onRate(i)}
          style={interactive ? { cursor: "pointer" } : {}}
        />
      ))}
    </div>
  );

  // Dynamic Popup
  if (showPopup && popup) {
    return (
      <div className="sf-popup-overlay" data-testid="dynamic-popup" onClick={() => setShowPopup(false)}>
        <div className="sf-popup-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-popup-close" onClick={() => setShowPopup(false)} data-testid="close-popup"><X size={20} /></button>
          {popup.link_url ? (
            <a href={popup.link_url} target="_blank" rel="noopener noreferrer">
              <img src={popup.image_url} alt="Promotion" className="sf-popup-img" />
            </a>
          ) : (
            <img src={popup.image_url} alt="Promotion" className="sf-popup-img" />
          )}
        </div>
      </div>
    );
  }

  // Auth Prompt Modal
  if (showAuthPrompt) {
    return (
      <div className="sf-popup-overlay" data-testid="auth-prompt" onClick={() => setShowAuthPrompt(false)}>
        <div className="sf-auth-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-popup-close" onClick={() => setShowAuthPrompt(false)}><X size={20} /></button>
          <div className="sf-auth-icon"><Lock size={32} /></div>
          <h3 className="sf-auth-title">Connexion requise</h3>
          <p className="sf-auth-desc">Connectez-vous ou creez un compte pour effectuer un achat.</p>
          <div className="sf-auth-btns">
            <button className="sf-btn sf-btn-primary" onClick={() => onNavigate("login-client")} data-testid="auth-login-btn">
              <LogIn size={18} /> Se connecter
            </button>
            <button className="sf-btn sf-btn-outline" onClick={() => onNavigate("register-client")} data-testid="auth-register-btn">
              <UserPlus size={18} /> Creer un compte
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Quick Buy Modal — LEGACY guarded path. We always redirect logged-in
  // clients to /client in handleBuyClick, but if any code path still triggers
  // showQuickBuy for an authenticated user, immediately bail out and route
  // them to the dashboard instead of rendering the legacy transaction_id form.
  if (showQuickBuy && selectedService && selectedPlan) {
    if (isClientLoggedIn()) {
      const sid = selectedService?.id || "";
      const pid = selectedPlan?.id || "";
      const query = (sid || pid) ? `?service_id=${encodeURIComponent(sid)}&plan_id=${encodeURIComponent(pid)}` : "";
      // Defer so React isn't mutating route state during render.
      setTimeout(() => { setShowQuickBuy(false); onNavigate("client", query); }, 0);
      return null;
    }
    const price = calculatePrice();
    return (
      <div className="sf-page" data-testid="quick-buy-page">
        <header className="sf-header">
          <button onClick={() => setShowQuickBuy(false)} className="sf-back-btn" data-testid="quick-buy-back">
            <ChevronLeft size={20} /> Retour
          </button>
          <h1 className="sf-logo">Paiement</h1>
          <div style={{ width: 40 }} />
        </header>
        <div className="sf-quickbuy-content">
          <div className="sf-qb-card" data-testid="quick-buy-card">
            <div className="sf-qb-header">
              <img src={selectedService.image_url || SERVICE_IMAGES[selectedService.id] || HERO_BG} alt="" className="sf-qb-img" />
              <div>
                <h3 className="sf-qb-name">{selectedService.name}</h3>
                <p className="sf-qb-plan">{selectedPlan.name}</p>
              </div>
            </div>

            {selectedPlan.price === 0 && selectedPlan.rate && !selectedPlan.is_exchange && (
              <div className="sf-qb-section">
                <label className="sf-label">Montant en USD {selectedPlan.min_usd ? `(min ${selectedPlan.min_usd}$)` : ""}</label>
                <input type="number" className="sf-input" placeholder="Montant en $"
                  value={customAmountUsd} onChange={e => setCustomAmountUsd(e.target.value)}
                  data-testid="qb-custom-amount" />
                <span className="sf-rate-info">Taux global : {Number(exchangeRate).toFixed(0)} HTG/USD</span>
              </div>
            )}

            {selectedPlan.is_exchange && (
              <div className="sf-qb-section">
                <label className="sf-label">Montant en Gdes (min 250)</label>
                <input type="number" className="sf-input" placeholder="Montant en Gdes"
                  value={customAmountUsd} onChange={e => setCustomAmountUsd(e.target.value)}
                  data-testid="qb-exchange-amount" />
                {customAmountUsd && parseFloat(customAmountUsd) >= 250 && (
                  <div className="sf-exchange-summary">
                    <div className="sf-exch-row"><span>Montant:</span><span>{parseFloat(customAmountUsd).toLocaleString()} Gdes</span></div>
                    <div className="sf-exch-row sf-exch-total"><span>Total:</span><strong>{price.toLocaleString()} Gdes</strong></div>
                  </div>
                )}
              </div>
            )}

            <div className="sf-qb-total">
              <span>Total a payer:</span>
              <strong>
                {price > 0
                  ? (selectedService?.id === "echange_moncash_natcash"
                      ? `${price.toLocaleString()} Gdes`
                      : <>{formatUSD(price, exchangeRate)} <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontWeight: 400 }}>(≈ {formatHTG(price)})</span></>)
                  : "---"}
              </strong>
            </div>

            <div className="sf-qb-section">
              <label className="sf-label">Mode de paiement</label>
              <div className="sf-pay-methods">
                <button className={`sf-method-btn ${paymentMethod === "qr" ? "active" : ""}`}
                  onClick={() => setPaymentMethod("qr")} data-testid="qb-method-qr">QR Code</button>
                <button className={`sf-method-btn ${paymentMethod === "phone" ? "active" : ""}`}
                  onClick={() => setPaymentMethod("phone")} data-testid="qb-method-phone"><Phone size={14} /> Telephone</button>
              </div>
            </div>

            {paymentMethod === "qr" ? (
              <div className="sf-qr-section" data-testid="qb-qr-section">
                <p className="sf-qr-instr">Scannez un QR Code pour payer</p>
                <div className="sf-qr-scroll">
                  {QR_CODES.map((qr, i) => (
                    <div key={qr.number || qr.label || `qr-${i}`} className="sf-qr-card">
                      <img src={qr.image} alt={qr.label} className="sf-qr-img" />
                      <div className="sf-qr-info"><span className="sf-qr-name">{qr.name}</span><span className="sf-qr-num">{qr.number}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="sf-phone-pay" data-testid="qb-phone-section">
                <p className="sf-qr-instr">Envoyez le paiement au numero:</p>
                <div className="sf-phone-num">{PAYMENT_PHONE}</div>
              </div>
            )}

            <div className="sf-qb-section">
              <label className="sf-label">ID de Transaction</label>
              <input type="text" className="sf-input" placeholder="Entrez l'ID de votre transaction"
                value={transactionId} onChange={e => setTransactionId(e.target.value)} data-testid="qb-transaction-id" />
            </div>

            <button className="sf-btn sf-btn-whatsapp sf-btn-full"
              onClick={handleSubmitOrder} disabled={isSubmitting || !transactionId.trim() || (selectedPlan.price === 0 && !selectedPlan.is_exchange && !customAmountUsd)}
              data-testid="qb-submit-order">
              <MessageCircle size={18} />
              {isSubmitting ? "Envoi..." : "J'ai paye - Envoyer via WhatsApp"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Service Detail
  if (selectedService && !showQuickBuy) {
    return (
      <div className="sf-page" data-testid="service-detail-page">
        <header className="sf-header">
          <button onClick={() => setSelectedService(null)} className="sf-back-btn" data-testid="service-back-btn">
            <ChevronLeft size={20} /> Retour
          </button>
          <h1 className="sf-logo">{selectedService.name}</h1>
          <div style={{ width: 40 }} />
        </header>
        <div className="sf-detail-content">
          <div className="sf-detail-hero">
            <img src={selectedService.image_url || SERVICE_IMAGES[selectedService.id] || HERO_BG} alt={selectedService.name} className="sf-detail-img" />
            <div className="sf-detail-overlay">
              <h2 className="sf-detail-name">{selectedService.name}</h2>
              {selectedService.subtitle && <p className="sf-detail-sub">{selectedService.subtitle}</p>}
              {/* Rate label removed — global rate is the single source of truth. */}
            </div>
          </div>
          <div className="sf-plans-list" data-testid="plans-list">
            {selectedService.plans.map(plan => (
              <button key={plan.id} className="sf-plan-row" onClick={() => handleBuyClick(selectedService, plan)} data-testid={`plan-${plan.id}`}>
                <div className="sf-plan-info">
                  <span className="sf-plan-name">{plan.name}</span>
                  <span className="sf-plan-price">
                    {plan.price > 0
                      ? (selectedService.id === "echange_moncash_natcash"
                          ? `${plan.price.toLocaleString()} Gdes`
                          : formatUSD(plan.price, exchangeRate))
                      : "Personnalise"}
                  </span>
                </div>
                <div className="sf-plan-buy">
                  <Zap size={14} /> Acheter
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main Storefront
  return (
    <div className="sf-page" data-testid="storefront-page">
      {/* Navigation */}
      <header className="sf-header" data-testid="sf-header">
        <h1 className="sf-logo">SMARTECH</h1>
        <div className="sf-search-wrap">
          <Search size={16} className="sf-search-icon" />
          <input type="text" placeholder="Rechercher un service..." className="sf-search"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="sf-search" />
        </div>
        <div className="sf-header-right">
          <div className="sf-settings-wrap">
            <button onClick={() => setShowSettings(!showSettings)} className="sf-settings-btn" data-testid="sf-settings-btn">
              {isClientLoggedIn() ? <User size={20} /> : <Settings size={20} />}
            </button>
            {showSettings && (
              <div className="sf-dropdown" data-testid="sf-settings-dropdown">
                {isClientLoggedIn() ? (
                  <>
                    <button onClick={() => onNavigate("client")} className="sf-dropdown-item" data-testid="goto-client-dashboard">
                      <ShoppingCart size={16} /> Mon Espace
                    </button>
                    <button onClick={() => { localStorage.removeItem("client_token"); localStorage.removeItem("client_user"); setShowSettings(false); toast.success("Deconnecte"); }}
                      className="sf-dropdown-item" data-testid="sf-logout">
                      <X size={16} /> Deconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => onNavigate("login-client")} className="sf-dropdown-item" data-testid="goto-login">
                      <LogIn size={16} /> Se connecter
                    </button>
                    <button onClick={() => onNavigate("register-client")} className="sf-dropdown-item" data-testid="goto-register">
                      <UserPlus size={16} /> Creer un compte
                    </button>
                    <div className="sf-dropdown-divider" />
                    <button onClick={() => onNavigate("login-affiliate")} className="sf-dropdown-item sf-dropdown-muted" data-testid="goto-affiliate-login">
                      <User size={16} /> Espace Affilie
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="sf-hero" data-testid="sf-hero">
        <div className="sf-hero-overlay">
          <h2 className="sf-hero-title">Services Numeriques Premium</h2>
          <p className="sf-hero-sub">Netflix, Free Fire, PayPal, Crypto et plus encore. Paiement facile via MonCash.</p>
          <button className="sf-btn sf-btn-primary sf-hero-cta" onClick={() => document.getElementById("services-section")?.scrollIntoView({ behavior: "smooth" })} data-testid="sf-hero-cta">
            <Eye size={18} /> Voir les services
          </button>
        </div>
      </section>

      {/* Exchange-rate banner — single source of truth, shown discreetly */}
      <div data-testid="sf-rate-banner" style={{ background: "#0F172A", color: "#FFF", textAlign: "center", padding: "0.5rem 1rem", fontSize: "0.78rem", letterSpacing: 0.3 }}>
        <strong>Tous les prix sont en USD ($).</strong>
        <span style={{ opacity: 0.75, marginLeft: 6 }}>1 USD = {Number(exchangeRate).toFixed(0)} HTG</span>
      </div>

      {/* Explainer Video - above services */}
      {popupVideo && (
        <section style={{ padding: "1rem", maxWidth: "500px", margin: "0 auto" }}>
          {popupVideo.title && <h3 style={{ textAlign: "center", fontSize: "0.95rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem" }}>{popupVideo.title}</h3>}
          <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <iframe src={popupVideo.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").replace("vimeo.com/", "player.vimeo.com/video/")}
              style={{ width: "100%", height: "250px", border: "none" }} allowFullScreen title="Vidéo explicative" />
          </div>
        </section>
      )}

      {/* Services Grid */}
      <section className="sf-services" id="services-section" data-testid="sf-services-section">
        <h2 className="sf-section-title">Nos Services</h2>
        <div className="sf-grid" data-testid="sf-services-grid">
          {filteredServices.map(service => {
            const pricing = getLowestPrice(service);
            const isOutOfStock = !!service.out_of_stock;
            return (
              <div
                key={service.id}
                className="sf-card"
                onClick={() => {
                  if (isOutOfStock) return;
                  setSelectedService(service);
                }}
                data-testid={`sf-card-${service.id}`}
                style={isOutOfStock ? { opacity: 0.7, cursor: "not-allowed", position: "relative" } : { position: "relative" }}
              >
                <div className="sf-card-img-wrap">
                  <img src={service.image_url || SERVICE_IMAGES[service.id] || HERO_BG} alt={service.name} className="sf-card-img" loading="lazy" />
                  {isOutOfStock && (
                    <span
                      data-testid={`sf-out-of-stock-${service.id}`}
                      className="sf-card-badge"
                      style={{ background: "#DC2626", color: "white", textTransform: "uppercase", letterSpacing: "0.04em" }}
                    >
                      Indisponible
                    </span>
                  )}
                  {!isOutOfStock && service.plans.length > 1 && <span className="sf-card-badge">{service.plans.length} options</span>}
                </div>
                <div className="sf-card-body">
                  <h3 className="sf-card-title">{service.name}</h3>
                  {service.subtitle && <p className="sf-card-sub">{service.subtitle}</p>}
                  <div className="sf-card-footer">
                    <span className="sf-card-price">
                      {pricing?.type === "fixed"
                        ? (service.id === "echange_moncash_natcash"
                            ? `${pricing.price.toLocaleString()} Gdes`
                            : formatUSD(pricing.price, exchangeRate))
                        : pricing?.type === "rate"
                          ? `Taux personnalisé`
                          : "Sur mesure"}
                    </span>
                    <button
                      className="sf-card-buy"
                      onClick={e => { e.stopPropagation(); if (isOutOfStock) return; handleBuyClick(service, service.plans[0]); }}
                      data-testid={`sf-buy-${service.id}`}
                      disabled={isOutOfStock}
                      style={isOutOfStock ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                    >
                      <Zap size={14} /> {isOutOfStock ? "Indisponible" : "Acheter"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Formations Section */}
      {publicCourses.length > 0 && (
        <section className="sf-services" data-testid="sf-courses-section" style={{ background: "#F8FAFC" }}>
          <h2 className="sf-section-title"><BookOpen size={20} style={{ verticalAlign: "middle", marginRight: 6 }} /> Formations en Ligne</h2>
          <div className="sf-grid" data-testid="sf-courses-grid">
            {publicCourses.map(course => (
              <div key={course.id} className="sf-card" data-testid={`sf-course-${course.id}`}
                onClick={() => {
                  if (isClientLoggedIn()) onNavigate("client");
                  else setShowAuthPrompt(true);
                }}>
                <div className="sf-card-img-wrap">
                  {course.image_url ? (
                    <img src={course.image_url} alt={course.title} className="sf-card-img" loading="lazy" />
                  ) : (
                    <div style={{ width: "100%", height: "100%", minHeight: 140, background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <BookOpen size={40} color="white" />
                    </div>
                  )}
                  <span className="sf-card-badge"><Video size={10} /> {course.video_count} videos</span>
                </div>
                <div className="sf-card-body">
                  <h3 className="sf-card-title">{course.title}</h3>
                  {course.description && <p className="sf-card-sub">{course.description.substring(0, 60)}{course.description.length > 60 ? "..." : ""}</p>}
                  <div className="sf-card-footer">
                    <span className="sf-card-price">{course.price?.toLocaleString()} Gdes</span>
                    <button className="sf-card-buy" onClick={e => {
                      e.stopPropagation();
                      if (isClientLoggedIn()) onNavigate("client");
                      else setShowAuthPrompt(true);
                    }} data-testid={`sf-buy-course-${course.id}`}>
                      <Zap size={14} /> Acheter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="sf-reviews" data-testid="sf-reviews-section">
        <h2 className="sf-section-title">Avis Clients</h2>
        {reviews.length > 0 ? (
          <div className="sf-reviews-scroll">
            {reviews.map((r, i) => (
              <div key={r._id || r.id || `${r.name || 'rev'}-${i}`} className="sf-review-card" data-testid={`review-${i}`}>
                <div className="sf-review-head">
                  <div className="sf-review-avatar">{(r.name || "C")[0].toUpperCase()}</div>
                  <div>
                    <div className="sf-review-name">{r.name || "Client"}</div>
                    <StarRating rating={r.rating} />
                  </div>
                </div>
                <p className="sf-review-text">{r.comment}</p>
                <span className="sf-review-date">{formatDate(r.created_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="sf-empty-reviews">Aucun avis pour le moment. Soyez le premier!</p>
        )}
        <button className="sf-btn sf-btn-outline sf-review-cta" onClick={() => isClientLoggedIn() ? setShowReviewForm(true) : setShowAuthPrompt(true)} data-testid="write-review-btn">
          <Star size={16} /> Laisser un avis
        </button>
      </section>

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="sf-popup-overlay" onClick={() => setShowReviewForm(false)}>
          <div className="sf-review-modal" onClick={e => e.stopPropagation()} data-testid="review-form-modal">
            <button className="sf-popup-close" onClick={() => setShowReviewForm(false)}><X size={20} /></button>
            <h3 className="sf-modal-title">Votre Avis</h3>
            <div className="sf-review-stars-select">
              <StarRating rating={reviewRating} interactive onRate={setReviewRating} />
            </div>
            <input type="text" className="sf-input" placeholder="Votre nom (optionnel)"
              value={reviewName} onChange={e => setReviewName(e.target.value)} data-testid="review-name-input" />
            <textarea className="sf-textarea" placeholder="Votre commentaire..." rows={4}
              value={reviewComment} onChange={e => setReviewComment(e.target.value)} data-testid="review-comment-input" />
            <button className="sf-btn sf-btn-primary sf-btn-full" onClick={handleSubmitReview} data-testid="submit-review-btn">
              Envoyer mon avis
            </button>
          </div>
        </div>
      )}

      {/* Floating WhatsApp — official group */}
      <a href="https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?mode=gi_t" target="_blank" rel="noopener noreferrer" className="sf-fab-whatsapp" data-testid="whatsapp-fab">
        <MessageCircle size={24} />
      </a>
    </div>
  );
};

export default StoreFront;
